import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Plus, Calendar as CalendarIcon, Receipt, DollarSign,
  ChevronRight, Trash2,
} from "lucide-react";

// Utils
function isoToBr(iso) {
  if (!iso) return "—";
  const s = String(iso).split("T")[0];
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function money(v) {
  if (v == null) return "$ 0.00";
  const n = Number(v);
  if (Number.isNaN(n)) return "$ 0.00";
  return "$ " + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function weekBoundsToday() {
  // Segunda a domingo, em ISO
  const now = new Date();
  const day = now.getDay(); // 0=Dom,1=Seg,...
  const diffToMonday = (day + 6) % 7; // Segunda = 0
  const monday = new Date(now);
  monday.setDate(now.getDate() - diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { start: fmt(monday), end: fmt(sunday) };
}

export default function Fechamento() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(() => {
    const { start, end } = weekBoundsToday();
    return { name: "", week_start: start, week_end: end, notes_free_text: "" };
  });
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fechamentos");
      setItems(data);
    } catch (e) {
      toast.error("Erro ao carregar fechamentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Informe um nome");
    if (!form.week_start || !form.week_end) return toast.error("Informe o período");
    if (form.week_end < form.week_start) return toast.error("Data final deve ser após a inicial");
    setCreating(true);
    try {
      const { data } = await api.post("/fechamentos", form);
      toast.success("Fechamento criado");
      setOpenCreate(false);
      const { start, end } = weekBoundsToday();
      setForm({ name: "", week_start: start, week_end: end, notes_free_text: "" });
      navigate(`/fechamento/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/fechamentos/${confirmDelete.id}`);
      toast.success("Fechamento removido");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const totalGeral = items.reduce((acc, it) => acc + (Number(it.total) || 0), 0);
  const notasGeral = items.reduce((acc, it) => acc + (it.notes_count || 0), 0);

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="fechamento-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
            <FileText className="w-3.5 h-3.5" /> Fechamento Semanal
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Consolidação de Notas</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Unifique todas as notas fiscais da semana em um único relatório. A IA extrai automaticamente data,
            veículo, VIN e valor total de cada nota anexada.
          </p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button size="lg" className="gap-2" data-testid="new-fechamento-btn">
              <Plus className="w-4 h-4" /> Novo Fechamento
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-fechamento-dialog">
            <DialogHeader>
              <DialogTitle>Novo Fechamento Semanal</DialogTitle>
              <DialogDescription>
                Defina um nome de referência (ex: cliente, seguradora ou período) e o intervalo de datas.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-1">
              <div>
                <Label htmlFor="fech-name">Nome / Referência</Label>
                <Input
                  id="fech-name"
                  data-testid="fech-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex.: Semana 06/2026 · Enterprise Insurance"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fech-start">Início da Semana</Label>
                  <Input
                    id="fech-start"
                    data-testid="fech-start-input"
                    type="date"
                    value={form.week_start}
                    onChange={(e) => setForm({ ...form, week_start: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="fech-end">Fim da Semana</Label>
                  <Input
                    id="fech-end"
                    data-testid="fech-end-input"
                    type="date"
                    value={form.week_end}
                    onChange={(e) => setForm({ ...form, week_end: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="fech-notes">Observações (opcional)</Label>
                <Textarea
                  id="fech-notes"
                  data-testid="fech-notes-input"
                  value={form.notes_free_text}
                  onChange={(e) => setForm({ ...form, notes_free_text: e.target.value })}
                  placeholder="Anotações internas que aparecerão no PDF..."
                  rows={3}
                />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setOpenCreate(false)} data-testid="fech-cancel-btn">
                  Cancelar
                </Button>
                <Button type="submit" disabled={creating} data-testid="fech-create-submit-btn">
                  {creating ? "Criando..." : "Criar Fechamento"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        <div className="rounded-xl border border-border bg-card p-4" data-testid="kpi-total-fechamentos">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> Fechamentos
          </div>
          <div className="text-2xl font-bold mt-1">{items.length}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="kpi-total-notas">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Receipt className="w-3 h-3" /> Notas processadas
          </div>
          <div className="text-2xl font-bold mt-1">{notasGeral}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="kpi-total-valor">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <DollarSign className="w-3 h-3" /> Valor consolidado
          </div>
          <div className="text-2xl font-bold mt-1">{money(totalGeral)}</div>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-16 text-center">Carregando fechamentos...</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/40 p-16 text-center">
          <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="text-lg font-semibold">Nenhum fechamento ainda</div>
          <div className="text-sm text-muted-foreground mt-1 mb-5 max-w-md mx-auto">
            Crie seu primeiro fechamento semanal e comece a anexar notas — a IA extrai os dados automaticamente.
          </div>
          <Button onClick={() => setOpenCreate(true)} data-testid="empty-create-fechamento-btn">
            <Plus className="w-4 h-4 mr-2" /> Criar Fechamento
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div
              key={it.id}
              className="group rounded-xl border border-border bg-card p-5 hover:border-primary/50 hover:shadow-md transition cursor-pointer relative"
              onClick={() => navigate(`/fechamento/${it.id}`)}
              data-testid={`fechamento-card-${it.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <CalendarIcon className="w-3 h-3" />
                    {isoToBr(it.week_start)} → {isoToBr(it.week_end)}
                  </div>
                  <div className="mt-1 font-semibold text-lg truncate" title={it.name}>{it.name}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground shrink-0" />
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Notas</div>
                  <div className="text-xl font-bold">{it.notes_count || 0}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
                  <div className="text-xl font-bold">{money(it.total)}</div>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground border-t border-border pt-3">
                <span>Criado em {isoToBr(it.created_at)}</span>
                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition"
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(it); }}
                  data-testid={`del-fechamento-${it.id}`}
                  aria-label="Excluir fechamento"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm Delete */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent data-testid="fech-delete-dialog">
          <DialogHeader>
            <DialogTitle>Excluir fechamento</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir <b>{confirmDelete?.name}</b>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} data-testid="fech-delete-cancel">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="fech-delete-confirm">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
